// https://projects.invisionapp.com/d/main#/console/19025613/400363202/preview

// grouped bar chart implementation informed by
// app/static/frontend/widgets/components/visualization/Bar.jsx
// https://observablehq.com/@d3/grouped-bar-chart
// https://keithpblog.org/post/upgrading-d3-from-v3-to-v4/
// https://medium.com/@nick3499/d3-scaleband-rangeround-padding-ordinal-scale-with-range-bands-including-padding-f4af1e3c96ab
// https://github.com/d3/d3/blob/master/CHANGES.md

// grouped horizontal bar chart implementation informed by
// https://observablehq.com/@slowkow/horizontal-grouped-bar-chart

import React, { useRef, useState } from "react"
import PropTypes from "prop-types"

// components
import { VisualizationBarWidget as StyledVisualizationBarWidget, Legend } from "QuorumDesign"

// d3 renderers
import {
    useGetXDomain,
    useGetXRangeRoundBands,
    useGetYDomain,
    useRenderSvg,
    useRenderXAxis,
    useRenderYAxis,
    useXRenderer,
    useYRenderer,
} from "app/static/frontend/widgets/components/visualization/renderers/sharedRenderers"
import { useRenderGroupedBars } from "app/static/frontend/widgets/components/visualization/renderers/bar/barGroupedRenderers"

// hooks
import { useRefDimensions } from "app/static/frontend/widgets/components/visualization/hooks/visualizationHooks"
import {
    getVisualizationOverrideHeight,
    getVisualizationOverrideWidth,
    getVisualizationXAxisLabelsLongestLength,
    useVisualizationMargin,
} from "app/static/frontend/widgets/components/visualization/hooks/visualizationAxesHooks"
import { useBarVisualizationData } from "app/static/frontend/widgets/components/visualization/hooks/bar/barHooks"

// constants
import dashboardConstants from "app/static/frontend/dashboards/constants"

export const BarGrouped = (props) => {
    const bar = useRef(null)
    const { height, width } = useRefDimensions({
        reference: bar,
        timestamp: props.timestamp,
    })

    const data = useBarVisualizationData({
        data: props.data,
        dataSources: props.dataSources,
        groupKey: props.groupKey,
        labels: props.labels,
        quorumDataType: props.quorumDataType,
        stancesWithCustomLabels: props.stancesWithCustomLabels,
    })
    const margin = useVisualizationMargin({
        data: props.data,
        dataSources: props.dataSources,
        height,
        width,
        isHorizontal: props.isHorizontal,
        quorumDataType: props.quorumDataType,
    })
    const xAxisLabelsLongestLength = getVisualizationXAxisLabelsLongestLength({
        data: props.data,
        dataSources: props.dataSources,
        height,
        width,
        isHorizontal: props.isHorizontal,
        quorumDataType: props.quorumDataType,
    })
    const [loadAll, setLoadAll] = useState(data.length < dashboardConstants.visualizationLoadAll)
    const barData = !loadAll ? data.slice(0, dashboardConstants.visualizationLoadAll) : data
    const overrideHeight =
        props.isHorizontal &&
        getVisualizationOverrideHeight({
            data: barData,
            height,
        })
    const overrideWidth =
        !props.isHorizontal &&
        getVisualizationOverrideWidth({
            data: barData,
            width,
        })

    const useRenderBarGrouped = () => {
        if (props.shouldOrderByDate) {
            barData.sort((a, b) => Date.parse(a.name) - Date.parse(b.name))
        }
        const x0DomainMemo = useGetXDomain({
            data: barData,
            groupKey: props.groupKey,
            timestamp: props.timestamp,
        })
        const x0RangeRoundBandsMemo = useGetXRangeRoundBands({
            // isHorizontal
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            margin,
            timestamp: props.timestamp,
            width: overrideWidth || width,
        })
        const x0RendererMemo = useXRenderer({
            domain: x0DomainMemo,
            // isHorizontal
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            rangeRoundBandsMemo: x0RangeRoundBandsMemo,
            timestamp: props.timestamp,
            width: overrideWidth || width,
        })

        const yDomainMemo = useGetYDomain({
            data: barData,
            keys: props.keys,
            timestamp: props.timestamp,
        })
        const yRendererMemo = useYRenderer({
            domain: yDomainMemo,
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            // necessary for the .range
            margin,
            timestamp: props.timestamp,
            // isHorizontal
            width: overrideWidth || width,
        })

        const showPercentage = [
            DjangIO.app.widgets.types.FrequencyTupleFields.open_rate.value,
            DjangIO.app.widgets.types.FrequencyTupleFields.click_rate.value,
        ].includes(props.dataSources[0].group_by?.frequency_tuple_field)

        useRenderXAxis({
            // grouped
            data: barData,
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            // necessary for the transform in xAxisRenderer
            percentage: showPercentage,
            margin,
            reference: bar,
            timestamp: props.timestamp,
            width: overrideWidth || width,
            xAxisLabelsLongestLength,
            xRendererMemo: props.isHorizontal ? yRendererMemo : x0RendererMemo,
        })

        useRenderYAxis({
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            margin,
            reference: bar,
            timestamp: props.timestamp,
            // necessary for the .innerTickSize in yAxisRenderer
            width: overrideWidth || width,
            yRendererMemo: props.isHorizontal ? x0RendererMemo : yRendererMemo,
        })

        const x1DomainMemo = useGetXDomain({
            groupKey: props.groupKey,
            keys: props.keys,
            timestamp: props.timestamp,
        })
        const x1RangeRoundBandsMemo = useGetXRangeRoundBands({
            groupKey: props.groupKey,
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            keys: props.keys,
            margin,
            timestamp: props.timestamp,
            width: overrideWidth || width,
            xRendererMemo: x0RendererMemo,
        })
        const x1RendererMemo = useXRenderer({
            domain: x1DomainMemo,
            // isHorizontal
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            rangeRoundBandsMemo: x1RangeRoundBandsMemo,
            timestamp: props.timestamp,
            width: overrideWidth || width,
        })
        useRenderGroupedBars({
            colors: props.colors,
            // grouped
            data: barData,
            dataSources: props.dataSources,
            editing: props.editing,
            editForm: props.editForm,
            showPercentage: showPercentage,
            // grouped
            groupKey: props.groupKey,
            height: overrideHeight || height,
            isExternal: props.isExternal,
            isHorizontal: props.isHorizontal,
            // grouped
            keys: props.keys,
            reference: bar,
            shouldOrderByDate: props.shouldOrderByDate,
            // for segueing from Bar onClick to safedSearch selection in search
            showInSearch: props.showInSearch,
            segueToProfileWithId: props.segueToProfileWithId,
            timestamp: props.timestamp,
            width: overrideWidth || width,
            x0: props.isHorizontal ? yRendererMemo : x0RendererMemo,
            x1: x1RendererMemo,
            y: props.isHorizontal ? x0RendererMemo : yRendererMemo,
        })
    }

    useRenderSvg({
        height,
        reference: bar,
        // BarGrouped
        overrideHeight,
        // BarGrouped
        overrideWidth,
        width,
    })

    useRenderBarGrouped()

    return (
        <React.Fragment>
            <StyledVisualizationBarWidget
                isHorizontal={props.isHorizontal}
                loadAll={loadAll}
                loadAllOnClick={() => {
                    setLoadAll(!loadAll)
                    props.updateTimestamp()
                }}
                noHeader={props.noHeader}
                // show the "Load More"/"Show Less" button if there are more than
                // visualizationLoadAll a-axis entries in the processed Bar Visualization data
                shouldRenderLoadAll={!props.editing && data.length > dashboardConstants.visualizationLoadAll}
            >
                <svg
                    data-cy="grouped-bar-visualization-widget"
                    ref={bar}
                    // ensure that the BarGrouped svg stretches to the height of the parent div
                    // this is important because we want the onResize to set correct values
                    // immediately after mount
                    style={{
                        flexGrow: 1,
                        ...((props.isHorizontal && loadAll) || overrideHeight
                            ? // svg element sets "overflow: hidden;" by default,
                              // which causes the horizontal grouped bar visualization
                              // to render erroneously in overflow mode
                              { overflow: "unset" }
                            : {}),
                    }}
                />
            </StyledVisualizationBarWidget>
            {props.colors && data && <Legend colors={props.colors} data={props.labels} />}
        </React.Fragment>
    )
}

BarGrouped.defaultProps = {
    // TODO: dummy colors
    // this will eventually come from the ColorPickerField(s) component
    colors: {
        0: "#A95AA1",
        1: "#503ABD",
        2: "#85C0F9",
    },
    // TODO: dummy data
    // this will eventually come from the FieldArray (index), labels (name), and data (value) from the evaluated SafedSearch
    data: [
        {
            20186: { agg: 1, groupKeyValue: "AL Rep. Howard Sanderford (R-AL-020)" },
            20338: { agg: 1, groupKeyValue: "AL Rep. Ritchie Whorton (R-AL-022)" },
            21168: { agg: 1, groupKeyValue: "AL Rep. Randall Shedd (R-AL-011)" },
            22110: { agg: 1, groupKeyValue: "AL Rep. Laura Hall (D-AL-019)" },
        },
        {
            20186: { agg: 2, groupKeyValue: "AL Rep. Howard Sanderford (R-AL-020)" },
            20338: { agg: 1, groupKeyValue: "AL Rep. Ritchie Whorton (R-AL-022)" },
            21168: { agg: 1, groupKeyValue: "AL Rep. Randall Shedd (R-AL-011)" },
            22110: { agg: 2, groupKeyValue: "AL Rep. Laura Hall (D-AL-019)" },
            1269249: { agg: 1, groupKeyValue: "AL Rep. Neil Rafferty (D-AL-054)" },
        },
    ],
    // key to group on
    groupKey: "name",
    // keys for grouped
    keys: ["Science", "Engineering"],
}
BarGrouped.propTypes = {
    colors: PropTypes.objectOf(PropTypes.string),
    // the WidgetResource widget_engine (app/widgets/api.py) returns:
    // an object if there is a single result
    // an array if there are multiple results
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    dataSources: PropTypes.array,
    editing: PropTypes.bool,
    editForm: PropTypes.bool,
    groupKey: PropTypes.string,
    isExternal: PropTypes.bool,
    shouldOrderByDate: PropTypes.bool,
    isHorizontal: PropTypes.bool,
    labels: PropTypes.array,
    noHeader: PropTypes.bool,
    keys: PropTypes.array,
    quorumDataType: PropTypes.object,
    // the timestamp is used for memoization in place of props.data
    // because it is less computationally intensive to compare
    timestamp: PropTypes.number,
    updateTimestamp: PropTypes.func,

    // action creators
    showInSearch: PropTypes.func,
}

export default BarGrouped
